import React, { useState, useEffect } from "react";
import {
  Container,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  Typography,
  Chip,
} from "@mui/material";
import _ from "lodash";
import ResultItem from "../components/ResultItem";
import StarBorder from "@mui/icons-material/StarBorder";
import { calculateFinalTax, calculateSurcharge } from "../utils/taxSurcharge";

function NewRegimeCalculator() {
  const [salary, setSalary] = useState("");
  const [employerPf, setEmployerPf] = useState("");
  const [employeePf, setEmployeePf] = useState("");
  const [isEmployerPfSameAsEmployee, setIsEmployerPfSameAsEmployee] =
    useState(false);
  const [isPfPartOfSalary, setIsPfPartOfSalary] = useState(false);
  const [results, setResults] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);

  useEffect(() => {
    const savedFavorites = localStorage.getItem("newRegimeFavorites");
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("newRegimeFavorites", JSON.stringify(favorites));
  }, [favorites]);

  const calculateTax = (taxableIncome) => {
    const slabs = [
      { limit: 400000, percent: 0 },
      { limit: 800000, percent: 5 },
      { limit: 1200000, percent: 10 },
      { limit: 1600000, percent: 15 },
      { limit: 2000000, percent: 20 },
      { limit: 2400000, percent: 25 },
      { limit: Infinity, percent: 30 },
    ];

    let tax = 0;
    let remaining = taxableIncome;
    let previousLimit = 0;

    for (const slab of slabs) {
      if (remaining <= 0) break;

      const slabAmount = Math.min(slab.limit - previousLimit, remaining);
      tax += slabAmount * (slab.percent / 100);
      remaining -= slabAmount;
      previousLimit = slab.limit;
    }

    // Calculate rebate for taxable income <= 12 lakh
    const rebate = taxableIncome <= 1200000 ? tax : 0;
    return { tax: tax - rebate, rebate };
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let inHandYear;

    const totalPfContribution = parseFloat(employeePf) + parseFloat(employerPf);
    const annualSalary = parseFloat(salary);
    const standardDeduction = 75000;

    let taxableIncome = annualSalary - standardDeduction;

    if (isPfPartOfSalary) {
      taxableIncome = taxableIncome - parseFloat(employerPf);
    }

    let { tax: baseTax, rebate } = calculateTax(taxableIncome);
    let marginalTaxRelief = "No";

    if (baseTax >= 60000 && taxableIncome <= 1260000) {
      baseTax = taxableIncome - 1200000;
      marginalTaxRelief = "Yes";
    }

    const { surcharge, surchargeRate, surchargeApplies, surchargeMarginalRelief } =
      calculateSurcharge({
        taxableIncome,
        baseTax,
        calculateBaseTax: (income) => calculateTax(income).tax,
        regime: "new",
      });
    const { cess, professionalTax, finalTax } = calculateFinalTax({
      baseTax,
      surcharge,
    });

    if (isPfPartOfSalary) {
      inHandYear = annualSalary - finalTax - totalPfContribution;
    } else {
      inHandYear = annualSalary - finalTax - parseFloat(employeePf);
    }
    const inHandMonth = inHandYear / 12;

    setResults({
      annualSalary,
      taxableIncome,
      baseTax,
      surcharge,
      surchargeRate,
      surchargeApplies,
      surchargeMarginalRelief,
      finalTax,
      cess,
      professionalTax,
      rebate,
      inHandYear,
      inHandMonth,
      standardDeduction,
      marginalTaxRelief,
      totalPF: isPfPartOfSalary ? totalPfContribution : parseFloat(employeePf),
    });
  };

  const formatNumber = (value) => {
    if (_.isEmpty(value)) return "";
    const number = _.toInteger(value.replace(/,/g, ""));
    return _.isNaN(number) ? "" : number.toLocaleString("en-IN");
  };

  const saveAsFavorite = () => {
    const favoriteName = window.prompt("Enter a name for this favorite:");
    if (!favoriteName) return;

    const newFavorite = {
      id: Date.now(),
      name: favoriteName || `Unnamed Favorite ${favorites.length + 1}`,
      values: {
        salary,
        employerPf,
        employeePf,
        isEmployerPfSameAsEmployee,
        isPfPartOfSalary,
      },
    };
    setFavorites([...favorites, newFavorite]);
  };

  const loadFavorite = (fav) => {
    const values = fav.values;
    setSalary(values.salary);
    setEmployerPf(values.employerPf);
    setEmployeePf(values.employeePf);
    setIsEmployerPfSameAsEmployee(
      values.isEmployerPfSameAsEmployee ?? false
    );
    setIsPfPartOfSalary(values.isPfPartOfSalary);
  };

  const deleteFavorite = (id) => {
    if (window.confirm("Are you sure you want to delete this favorite?")) {
      setFavorites(favorites.filter((fav) => fav.id !== id));
    }
  };

  return (
    <Container component="main" sx={{ flex: 1, py: 4 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          mt: 3,
          pb: 2,
          color: "secondary.main",
          fontWeight: 700,
          borderColor: "secondary.light",
          textAlign: "center",
          textTransform: "uppercase",
          letterSpacing: 1.2,
          mb: 3,
        }}
      >
        🚀 New Tax Regime Calculator
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Annual Gross Salary"
              variant="outlined"
              type="text"
              value={formatNumber(salary)}
              helperText="Annual gross salary is your total yearly salary. It includes basic pay and salary components such as HRA and allowances."
              onChange={(e) => {
                const rawValue = _.replace(e.target.value, /[^0-9]/g, "");
                if (rawValue === "" || /^\d+$/.test(rawValue)) {
                  setSalary(rawValue);
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Employee PF Contribution Annually"
              variant="outlined"
              type="text"
              value={formatNumber(employeePf)}
              onChange={(e) => {
                const rawValue = _.replace(e.target.value, /[^0-9]/g, "");
                if (rawValue === "" || /^\d+$/.test(rawValue)) {
                  setEmployeePf(rawValue);
                  if (isEmployerPfSameAsEmployee) setEmployerPf(rawValue);
                }
              }}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Employer PF Contribution Annually"
              variant="outlined"
              type="text"
              value={formatNumber(employerPf)}
              onChange={(e) => {
                const rawValue = _.replace(e.target.value, /[^0-9]/g, "");
                if (rawValue === "" || /^\d+$/.test(rawValue)) {
                  setEmployerPf(rawValue);
                }
              }}
              disabled={isEmployerPfSameAsEmployee}
              required
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isEmployerPfSameAsEmployee}
                  onChange={(e) => {
                    const isSame = e.target.checked;
                    setIsEmployerPfSameAsEmployee(isSame);
                    if (isSame) setEmployerPf(employeePf);
                  }}
                />
              }
              label="Same as employee PF?"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPfPartOfSalary}
                  onChange={(e) => setIsPfPartOfSalary(e.target.checked)}
                />
              }
              label="Is Employ(er) PF part of the salary mentioned above?"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              * Standard deduction of ₹75,000 is applied automatically
            </Typography>
          </Grid>

          <Grid item xs={12} sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!salary || !employerPf || !employeePf}
            >
              Calculate
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={saveAsFavorite}
              disabled={!salary}
              startIcon={<StarBorder />}
            >
              Save Favorite
            </Button>
          </Grid>
        </Grid>
      </form>

      {results && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Calculation Results
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Card
                variant="outlined"
                sx={{
                  borderColor: "divider",
                  boxShadow: 1,
                  height: "100%",
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "primary.main",
                      mb: 1,
                    }}
                  >
                    Tax Details
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={1}>
                    <ResultItem
                      label="Annual gross salary"
                      value={results.annualSalary}
                      sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1 }}
                    />
                    <ResultItem
                      label="Total Taxable Income"
                      value={results.taxableIncome}
                      sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1 }}
                    />
                    <ResultItem
                      label="Total PF Paid"
                      value={results.totalPF}
                      title={`Your salary ${isPfPartOfSalary ? "includes" : "does not include"
                        } employer PF contribution`}
                      sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1 }}
                    />
                  </Grid>

                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    <ResultItem
                      label="Rebate"
                      value={results.rebate}
                      sx={{ bgcolor: "success.light", borderRadius: 1, p: 1 }}
                    />
                    <ResultItem
                      label="Marginal Tax Relief"
                      value={results.marginalTaxRelief}
                      sx={{ bgcolor: "error.light", borderRadius: 1, p: 1 }}
                    />
                    <ResultItem
                      label="Surcharge"
                      value={results.surchargeApplies ? "Yes" : "No"}
                      sx={{ bgcolor: "error.light", borderRadius: 1, p: 1 }}
                    />
                    <ResultItem
                      label="Total Tax"
                      value={results.baseTax}
                      sx={{ bgcolor: "error.light", borderRadius: 1, p: 1 }}
                    />
                    {results.surchargeApplies && (
                      <ResultItem
                        label={`Surcharge (${results.surchargeRate}%)`}
                        value={results.surcharge}
                        sx={{ bgcolor: "error.light", borderRadius: 1, p: 1 }}
                      />
                    )}
                    {results.cess > 0 && (
                      <ResultItem
                        label="Health & Education Cess (4%)"
                        value={results.cess}
                        sx={{ bgcolor: "error.light", borderRadius: 1, p: 1 }}
                      />
                    )}
                    {results.professionalTax > 0 && (
                      <ResultItem
                        label="Professional Tax"
                        value={results.professionalTax}
                        sx={{ bgcolor: "error.light", borderRadius: 1, p: 1 }}
                      />
                    )}
                    <ResultItem
                      label="Final Tax"
                      value={results.finalTax}
                      sx={{ bgcolor: "error.light", borderRadius: 1, p: 1 }}
                    />
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card
                variant="outlined"
                sx={{
                  boxShadow: 3,
                  border: 2,
                  borderColor: "primary.main",
                }}
              >
                <CardContent>
                  <Typography variant="h6" color="primary">
                    In-hand Salary
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gross pay − PF contributions − final tax
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={1}>
                    <ResultItem label="Annual" value={results.inHandYear} />
                    <ResultItem label="Monthly" value={results.inHandMonth} />
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {favorites.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Saved Favorites
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {favorites.map((fav) => (
              <Chip
                key={fav.id}
                label={fav.name}
                onClick={() => loadFavorite(fav)}
                onDelete={() => deleteFavorite(fav.id)}
                color="primary"
                variant="outlined"
                sx={{ cursor: "pointer" }}
              />
            ))}
          </Box>
        </Grid>
      )}
    </Container>
  );
}

export default NewRegimeCalculator;
